<template>
  <div>
    <Modal
      :show="modalVisible"
      fullscreen
      @close="onClose"
    >
      <template #title>
        Generate content for {{ title }}
      </template>

      <template #content>
        <div class="w-100 d-flex ga-16 text-textDarkest">
          <div class="w-100">
            <div class="right-column-header mb-4">
              Original description
            </div>
            <RevisionContent
              :fields="existingBlocksAsFields"
            />
          </div>

          <div class="w-100">
            <div
              v-if="isNewContentLoading"
              class="h-100"
            >
              <FirstDraftProgress
                class="h-100 w-100 position-relative"
              />
            </div>
            <div v-else>
              <EditorToolbarContainer
                :show-comments="false"
              />
              <DescriptionEditor
                class="mt-4 new-content-editor"
                :value="newContent"
                editor-id="first-draft-new-content"
                :placeholder="'Click Generate for new content'"
                :focused-editor-id="'first-draft-new-content'"
                :disabled-nodes="{ link: true }"
                @add-editor="onAddEditor"
                @blur-editor="onBlurEditor"
                @remove-editor="onRemoveEditor"
                @focus-editor="onFocusEditor"
                @update:model-value="onContentUpdate"
              />
            </div>
          </div>
        </div>
      </template>

      <template #actions>
        <div class="w-100 d-flex justify-space-between ga-16">
          <div class="w-100" />
          <div class="w-100 d-flex justify-space-between">
            <div>
              <DpplAutoSizedButton
                :disabled="isNewContentLoading || !newContent"
                @click="onApplyClick"
              >
                Apply
              </DpplAutoSizedButton>

              <FirstDraftButton
                :disabled="isNewContentLoading || !newContent"
                class="pa-0 ml-4"
                variant="plain"
                @click="onGenerateClick"
                height="24"
              >
                <span class="text-button-2">
                  Re-Iterate with First Draft
                </span>
              </FirstDraftButton>
            </div>
            <div class="d-flex flex-column justify-center">
              <v-btn
                variant="text"
                color="error"
                @click="onClose"
              >
                Close
              </v-btn>
            </div>
          </div>
        </div>
      </template>
    </Modal>
  </div>
</template>

<script>
import { mapActions, mapState } from 'pinia';
import { useDocumentEditor } from '@/stores/document/editor';
import { useFirstDraft } from '@/stores/document/firstDraft';
import Modal from '@/components/shared/Modal.vue';
import DpplAutoSizedButton from '@/components/datapeople/DpplAutoSizedButton.vue';
import DescriptionEditor from '@ui/components/editor/DescriptionEditor/DescriptionEditor.vue';
import EditorToolbarContainer
  from '@/components/document/editorToolbar/EditorToolbarContainer.vue';
import FirstDraftButton from '@ui/components/firstDraft/FirstDraftButton.vue';
import FirstDraftProgress from '@/components/firstDraft/FirstDraftProgress.vue';
import { useTracking } from '@/stores/tracking/tracking';
import RevisionContent from '@/components/editor/RevisionContent.vue';

export default {
  name: 'FirstDraftModal',

  components: {
    RevisionContent,
    DescriptionEditor,
    DpplAutoSizedButton,
    EditorToolbarContainer,
    FirstDraftButton,
    FirstDraftProgress,
    Modal,
  },

  computed: {
    ...mapState(useFirstDraft, [
      'existingBlocks',
      'newContent',
      'modalVisible',
      'isNewContentLoading',
    ]),
    ...mapState(useDocumentEditor, ['title']),

    existingBlocksAsFields() {
      return [
        {
          id: 'field1',
          type: 'default',
          label: '',
          sections: [
            {
              id: 'section1',
              blocks: this.existingBlocks,
            },
          ],
        },
      ];
    },
  },

  mounted() {
    this.reset();
  },

  methods: {
    ...mapActions(useDocumentEditor, ['addTiptap', 'removeTiptap', 'updateFocus']),
    ...mapActions(useFirstDraft, [
      'setModalVisible',
      'generateNewContent',
      'updateNewContent',
      'reset',
      'applyNewContent',
    ]),
    ...mapActions(useTracking, ['trackName']),

    onGenerateClick() {
      this.trackName({ name: 'Clicked: Iterate with First Draft from First Draft Modal' });

      this.generateNewContent();
    },

    onApplyClick() {
      this.trackName({ name: 'Clicked: Apply from First Draft Modal' });

      this.applyNewContent();
      this.onClose({ trackEvent: false });
    },

    onContentUpdate({ html }) {
      this.updateNewContent(html);
    },

    onClose({ trackEvent = true } = {}) {
      if (trackEvent) {
        this.trackName({ name: 'Clicked: Close from First Draft Modal' });
      }

      this.setModalVisible(false);
    },
    onFocusEditor({ editorId }) {
      this.updateFocus(editorId);
    },
    onBlurEditor() {
      this.updateFocus('');
    },
    onAddEditor({ editorId, tiptap }) {
      this.addTiptap(editorId, tiptap);
    },
    onRemoveEditor({ editorId }) {
      this.removeTiptap(editorId);
    },
  },
};
</script>

<style scoped lang="less">
.new-content-editor {
  :deep(.tiptap) {
    min-height: 500px;
  }
}
.right-column-header {
  text-transform: uppercase;
  font-weight: 700;
  padding-top: 14px;
  padding-bottom: 14px;
  border-top: 1px solid @stroke-default;
  border-bottom: 1px solid @stroke-default;
}
.editor-toolbar {
  border-top: 1px solid @stroke-default;
  border-bottom: 1px solid @stroke-default;
}
</style>
